/**
 * Build the countertop object for a vanity assembly.
 *
 * Single source of the countertop emit convention: dimensions live in the
 * CABINET LOCAL frame (x = width along the cabinet, z = depth) and the
 * cabinet's rotation is carried verbatim — the same convention
 * assembleVanity.js uses. Never emit a countertop with rotation {0,0,0} and
 * world-axis-swapped dims: that only coincides with this convention at
 * exactly cardinal rotations, and a story whose alignment residual leaves
 * the grid a fraction of a degree off cardinal gets a countertop visibly
 * skewed off its own cabinet (structure B7D88301: 0.664° over a 3.3m
 * double vanity).
 */

const { computeCountertopOverhangs } = require('./placementUtils');

/**
 * @param {Object} options
 * @param {Object} options.cabinet - { id, center, dimensions, rotation }
 * @param {Object|null} options.parentWall - Cabinet's parent wall, or null
 * @param {Object} options.roomData - { walls, objects, doors, windows } for
 *        overhang suppression; only consulted when parentWall is present
 * @param {number} options.thickness - Countertop thickness (m)
 * @param {number} options.centerY - World Y of the countertop center
 * @param {string} options.fixtureGroupId
 * @param {string} options.addedBy
 * @param {string} [options.label] - Optional display label
 * @param {string} [options.textureId] - Optional countertop texture id
 * @param {string} [options.id] - Explicit id (default `${cabinet.id}_countertop`)
 * @param {number} [options.frontOverhang] - how far the slab reaches past the
 *        cabinet's FRONT face. Defaults to the 1" nosing; the scan path asks
 *        for more so the slab can carry a basin over a shallower carcass.
 * @returns {Object} countertop object
 */
function buildVanityCountertop({
  cabinet,
  parentWall,
  roomData,
  thickness,
  centerY,
  fixtureGroupId,
  addedBy,
  label,
  textureId,
  id,
  frontOverhang,
}) {
  const cabDims = cabinet.dimensions || { x: 0.9, y: 0.8, z: 0.5 };
  const cabCenter = cabinet.center || { x: 0, y: 0, z: 0 };

  let countertopWidth, countertopDepth, centerOffset;
  if (parentWall) {
    const overhangResult = computeCountertopOverhangs({
      cabinetWidth: cabDims.x,
      cabinetDepth: cabDims.z,
      cabinetCenter: cabCenter,
      parentWall,
      roomData,
      frontOverhangOverride: frontOverhang,
    });
    countertopWidth = overhangResult.countertopWidth;
    countertopDepth = overhangResult.countertopDepth;
    centerOffset = overhangResult.centerOffset;
  } else {
    countertopWidth = cabDims.x;
    countertopDepth = cabDims.z;
    centerOffset = { x: 0, z: 0 };
  }

  return {
    id: id ?? `${cabinet.id}_countertop`,
    type: 'countertop',
    ...(label ? { label } : {}),
    ...(textureId ? { texture: { textureId } } : {}),
    category: { countertop: true },
    center: {
      x: cabCenter.x + centerOffset.x,
      y: centerY,
      z: cabCenter.z + centerOffset.z,
    },
    dimensions: {
      x: countertopWidth,
      y: thickness,
      z: countertopDepth,
    },
    rotation: { x: 0, y: cabinet.rotation?.y ?? 0, z: 0 },
    parents: [{ id: cabinet.id, type: 'lower-cabinet' }],
    fixtureGroupId,
    addedBy,
    lastModified: new Date().toISOString(),
  };
}

module.exports = { buildVanityCountertop };
